/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import {GenericModelViewer} from "../Common/GenericModelViewer";
import {ConfirmationDialog} from "../Common/ConfirmationDialog";
import React, {useEffect, useMemo, useState} from "react";

import {Permit} from "../../Models/StreamBank/Permit";
import {ConservationActionPermit} from "../../Models/StreamBank/ConservationActionPermit";
import {usePrimeCache} from "../../Hooks/ServiceHooks";
import {service as PermitTypeService} from "../../Services/PermitTypeService";
import {service as PermitService} from "../../Services/PermitService";
import {useDefaultErrorHandler} from "../../Hooks/ErrorHooks";
import {Col, Row} from "react-bootstrap";
import {useValidations} from "../../Hooks/GenericModelHooks";

export const PermitModal = (props) => {
    const {
        show,
        permit: permitFromProps,
        surveyTypeId,
        editable,
        onHide = () => {},
        onUpdate = (conservationActionPermit) => {},
        // AdditionalTypeProps,
    } = props;
    
    const [error, setError] = useState(null);
    const [permit, setPermit] = useState(null);
    
    useEffect(() => {
        setPermit(permitFromProps);
    }, [permitFromProps, show]);
    
    const defaultErrorHandler = useDefaultErrorHandler();
    const [validations, setValidations, isValid] = useValidations();
    
    usePrimeCache([PermitTypeService]);
    
    const permitType = useMemo(() => {
        if(permit?.permitTypeId) {
            return PermitTypeService.CachedValueById(permit?.permitTypeId)?.result;
        }
        return null;
    }, [permit?.permitTypeId]);
    
    const permitId = permit?.permitId;
    const isNewPermit = !permitId;
    
    const save = async (permit) => {
        setError(null);
        try {
            const response = await (isNewPermit ? PermitService.CreateModel(permit) : PermitService.UpdateModel(permit));
            const updatedPermit = response?.result;
            if(updatedPermit) {
                onUpdate(updatedPermit);
                onHide();
            } else {
                throw response.exception ?? new Error(response?.message ?? 'Unknown Error occurred.');
            }
        } catch (error) {
            defaultErrorHandler(error);
        }
    }
    
    const dialogDescription = <span><span className="dialog-bold">This Information is Read-Only.</span> Edit it by clicking one of the Conservation Actions and then clicking 'Edit' at the top of the Conservation Action Permits Page.</span>;
    
    const title = useMemo(
        () => {
            const prefix = editable ? (isNewPermit ? 'Create Permit' : 'Edit Permit') : '';
            const permitTitle = PermitService.GetTitle(permit);
            
            return [prefix, permitTitle].join(' - ').trim();
        },
        [permit, editable]
    );
    
    // Alter the Permit model's 'ConservationActionPermits' association field def
    // so that it's model has a 'defaults' value that includes the current Permit's permitId
    // this ensures that when the ChildrenFieldEditor adds a new ConservationActionPermit,
    // it will automatically set its permitId to the current Permit
    const alteredPermitModel = useMemo(() => {
        const permitModel = new Permit();
        const alteredConservationActionPermitModel = new ConservationActionPermit();
        const permitConservationActionPermitsFieldDef = permitModel
            .getFlattenedFieldDefs()
            .find((field) => field.key === "ConservationActionPermits");
        
        alteredConservationActionPermitModel.defaults = {permitId};
        permitConservationActionPermitsFieldDef.model = alteredConservationActionPermitModel;
        
        return permitModel;
    }, [permitId]);
    
    return <ConfirmationDialog
        size="lg"
        Title={title}
        Body={[
            (error) &&
            <Row key="error-row">
                <Col xs={12}>
                    <p className="sb-model-viewer-validation-failure-text">{error}</p>
                </Col>
            </Row>,
            permit &&
            <GenericModelViewer
                Model={alteredPermitModel}
                IsNewModel={permitId === null}
                EditMode={editable}
                Data={permit}
                Update={setPermit}
                OnValidationUpdate={setValidations}
                CustomData={{surveyTypeId}} // FileEditors will use this to add appropriate File tags
            />
        ]}
        CancelButtonMessage={editable ? 'Cancel' : 'Dismiss'}
        OnHide={onHide}
        ConfirmationButtonMessage="Save Changes"
        Display={show}
        OnConfirm={editable ? () => save(permit) : null}
        DisableConfirmationButton={!isValid}
        DialogDescription={editable ? null : dialogDescription}
    />
}
